import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { Mail, Phone, CheckCircle, X, Globe, ChevronDown } from 'lucide-react'
import { LanguageCode, LANGUAGE_OPTIONS, DEFAULT_LANGUAGE } from '../config/language'
import { getApiUrl } from '../config/api'

interface ContactFormProps {
  primaryColor: string
  onSubmit: (email: string, phone: string, language: LanguageCode) => void
  onClose?: () => void
}

export const ContactForm: React.FC<ContactFormProps> = ({ primaryColor, onSubmit, onClose }) => {
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [emailError, setEmailError] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [isFlipped, setIsFlipped] = useState(false)
  const [consentChecked, setConsentChecked] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(DEFAULT_LANGUAGE)
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false)

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  // Phone validation: digits only, 9-15 characters
  const phoneRegex = /^[0-9]{9,15}$/

  // Validate email
  const validateEmail = (value: string): boolean => {
    if (!value) {
      setEmailError('נא למלא אימייל')
      return false
    }
    if (!emailRegex.test(value)) {
      setEmailError('נא למלא אימייל תקין')
      return false
    }
    setEmailError('')
    return true
  }

  // Validate phone
  const validatePhone = (value: string): boolean => {
    // Remove spaces, dashes, and any formatting
    const cleanPhone = value.replace(/[\s\-()]/g, '')

    if (!cleanPhone) {
      setPhoneError('נא למלא מספר טלפון')
      return false
    }
    if (!phoneRegex.test(cleanPhone)) {
      setPhoneError('נא למלא מספר טלפון תקין (9-15 ספרות)')
      return false
    }
    setPhoneError('')
    return true
  }

  // Handle form submission - flip to tips
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const isEmailValid = validateEmail(email)
    const cleanPhone = phone.replace(/[\s\-()]/g, '')
    const isPhoneValid = validatePhone(cleanPhone)

    if (isEmailValid && isPhoneValid) {
      setIsFlipped(true)
    }
  }

  // Handle "Let's start" button from tips side
  const handleStartConversation = () => {
    const cleanPhone = phone.replace(/[\s\-()]/g, '')
    onSubmit(email, cleanPhone, selectedLanguage)
  }

  // Check if form is valid (including consent checkbox)
  const isFormValid = email && phone && !emailError && !phoneError && consentChecked

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 10002,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      {/* Flip Card Container */}
      <div
        style={{
          perspective: '1000px',
          maxWidth: 420,
          width: '100%',
        }}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{
            scale: 1,
            opacity: 1,
            rotateY: isFlipped ? 180 : 0
          }}
          transition={{
            scale: { type: 'spring', stiffness: 300, damping: 30 },
            rotateY: { duration: 0.6, ease: 'easeInOut' }
          }}
          style={{
            position: 'relative',
            width: '100%',
            transformStyle: 'preserve-3d',
          }}
        >
          {/* Front Side - Contact Form */}
          <div
            style={{
              backfaceVisibility: 'hidden',
              backgroundColor: 'white',
              borderRadius: 16,
              padding: 32,
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              direction: 'rtl',
            }}
          >
        {/* Close Button */}
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 16,
              left: 16,
              width: 32,
              height: 32,
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#f3f4f6',
              color: '#6b7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e5e7eb'
              e.currentTarget.style.color = '#374151'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6'
              e.currentTarget.style.color = '#6b7280'
            }}
            aria-label="סגור"
          >
            <X size={18} />
          </button>
        )}

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${primaryColor}, ${adjustBrightness(primaryColor, 0.8)})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}
          >
            <CheckCircle size={32} color="white" />
          </div>
          <h2 style={{ margin: '0 0 8px', fontSize: 24, fontWeight: 700, color: '#1f2937' }}>
            ברוכים הבאים!
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: '#6b7280' }}>
            נא למלא את הפרטים שלך כדי להתחיל
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Email Field */}
          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="email"
              style={{
                display: 'block',
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: '#374151',
              }}
            >
              <Mail size={16} style={{ display: 'inline', marginLeft: 6, verticalAlign: 'middle' }} />
              אימייל
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (emailError) validateEmail(e.target.value)
              }}
              onBlur={() => validateEmail(email)}
              placeholder="example@email.com"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: emailError ? '2px solid #ef4444' : '2px solid #e5e7eb',
                fontSize: 15,
                color: '#1f2937',
                backgroundColor: '#ffffff',
                outline: 'none',
                transition: 'border-color 0.2s',
                direction: 'ltr',
                textAlign: 'left',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = primaryColor
              }}
              onBlurCapture={(e) => {
                if (!emailError) {
                  e.target.style.borderColor = '#e5e7eb'
                }
              }}
            />
            {emailError && (
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#ef4444' }}>
                {emailError}
              </p>
            )}
          </div>

          {/* Phone Field */}
          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="phone"
              style={{
                display: 'block',
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: '#374151',
              }}
            >
              <Phone size={16} style={{ display: 'inline', marginLeft: 6, verticalAlign: 'middle' }} />
              טלפון
            </label>
            <input
              id="phone"
              type="tel"
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value)
                if (phoneError) validatePhone(e.target.value)
              }}
              onBlur={() => validatePhone(phone)}
              placeholder="050-1234567"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: phoneError ? '2px solid #ef4444' : '2px solid #e5e7eb',
                fontSize: 15,
                color: '#1f2937',
                backgroundColor: '#ffffff',
                outline: 'none',
                transition: 'border-color 0.2s',
                direction: 'ltr',
                textAlign: 'left',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = primaryColor
              }}
              onBlurCapture={(e) => {
                if (!phoneError) {
                  e.target.style.borderColor = '#e5e7eb'
                }
              }}
            />
            {phoneError && (
              <p style={{ margin: '6px 0 0', fontSize: 13, color: '#ef4444' }}>
                {phoneError}
              </p>
            )}
          </div>

          {/* Language Picker - Glowing dropdown */}
          <div style={{ marginBottom: 20 }}>
            <label
              htmlFor="contactform-language-button"
              style={{
                display: 'block',
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: '#374151',
              }}
            >
              <Globe size={16} style={{ display: 'inline', marginLeft: 6, verticalAlign: 'middle' }} />
              שפת השיחה
            </label>
            <div style={{ position: 'relative' }}>
              <button
                id="contactform-language-button"
                type="button"
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: 8,
                  border: `2px solid ${primaryColor}`,
                  backgroundColor: 'white',
                  fontSize: 15,
                  color: '#1f2937',
                  outline: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  boxShadow: `0 0 12px ${primaryColor}40`,
                  animation: 'pulse-glow 2s ease-in-out infinite',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 20 }}>
                    {LANGUAGE_OPTIONS.find(l => l.code === selectedLanguage)?.flag}
                  </span>
                  <span>
                    {LANGUAGE_OPTIONS.find(l => l.code === selectedLanguage)?.label}
                  </span>
                </span>
                <ChevronDown
                  size={18}
                  style={{
                    color: '#6b7280',
                    transform: isLanguageDropdownOpen ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.2s',
                  }}
                />
              </button>

              {/* Dropdown menu */}
              {isLanguageDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: 4,
                    backgroundColor: 'white',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                    zIndex: 100,
                    overflow: 'hidden',
                  }}
                >
                  {LANGUAGE_OPTIONS.map((lang) => (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => {
                        setSelectedLanguage(lang.code)
                        setIsLanguageDropdownOpen(false)
                      }}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        border: 'none',
                        backgroundColor: selectedLanguage === lang.code ? `${primaryColor}15` : 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        fontSize: 14,
                        color: '#374151',
                        transition: 'background-color 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        if (selectedLanguage !== lang.code) {
                          e.currentTarget.style.backgroundColor = '#f9fafb'
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedLanguage !== lang.code) {
                          e.currentTarget.style.backgroundColor = 'white'
                        }
                      }}
                    >
                      <span style={{ fontSize: 18 }}>{lang.flag}</span>
                      <span>{lang.label}</span>
                      {selectedLanguage === lang.code && (
                        <CheckCircle size={16} style={{ marginRight: 'auto', marginLeft: 8, color: primaryColor }} />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <p style={{ margin: '6px 0 0', fontSize: 12, color: '#9ca3af' }}>
              השיחה תתנהל בשפה שנבחרה
            </p>
          </div>

          {/* Keyframe animation for glow effect */}
          <style>{`
            @keyframes pulse-glow {
              0%, 100% { box-shadow: 0 0 8px ${primaryColor}30; }
              50% { box-shadow: 0 0 20px ${primaryColor}50; }
            }
            input::placeholder {
              color: #9ca3af !important;
              opacity: 1 !important;
            }
          `}</style>

          {/* Consent Checkbox */}
          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                cursor: 'pointer',
                fontSize: 13,
                color: '#374151',
                lineHeight: 1.5,
              }}
            >
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                style={{
                  width: 18,
                  height: 18,
                  marginTop: 2,
                  accentColor: primaryColor,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              />
              <span style={{ direction: 'rtl', textAlign: 'right' }}>
                אני מאשר/ת שימוש במידע לצורך יצירת קשר, וידוע לי שהשיחה עשויה להיות מוקלטת ומנותחת
              </span>
            </label>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={!isFormValid}
            style={{
              width: '100%',
              padding: '14px 24px',
              borderRadius: 8,
              border: 'none',
              background: isFormValid
                ? `linear-gradient(135deg, ${primaryColor}, ${adjustBrightness(primaryColor, 0.8)})`
                : '#e5e7eb',
              color: isFormValid ? 'white' : '#9ca3af',
              fontSize: 16,
              fontWeight: 600,
              cursor: isFormValid ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
              opacity: isFormValid ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isFormValid) {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = `0 4px 12px ${primaryColor}40`
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            התחל שיחה
          </button>
        </form>

        {/* Privacy Notice */}
        <p style={{ margin: '16px 0 0', fontSize: 12, color: '#9ca3af', textAlign: 'center' }}>
          הפרטים שלך ישמרו באופן מאובטח ולא ישותפו עם צד שלישי
        </p>
          </div>

          {/* Back Side - Tips */}
          <div
            style={{
              backfaceVisibility: 'hidden',
              backgroundColor: 'white',
              borderRadius: 16,
              padding: 32,
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: 'rotateY(180deg)',
              background: 'linear-gradient(135deg, #ffffff 0%, #fef9f3 100%)',
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              direction: 'rtl',
            }}
          >
            {/* Tips Header */}
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}>💡</div>
              <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1f2937' }}>
                טיפים לשיחה מוצלחת
              </h2>
            </div>

            {/* Tips Grid - simplified to reduce cognitive load */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 16,
                marginBottom: 24,
              }}
            >
              {/* Tip 1 - Interrupt Bot */}
              <TipCard
                iconType="image"
                iconSrc={getApiUrl('/InteruputM.png')}
                title="הפסקת הסוכנת באמצע"
                description="הסוכנת תפסיק לדבר כשתתחילו"
              />

              {/* Tip 2 - Mute Microphone */}
              <TipCard
                iconType="image"
                iconSrc={getApiUrl('/MuteMIC.png')}
                title="השתקת מיקרופון"
                description="מכבה את המיקרופון שלכם"
              />

              {/* Tip 3 - Quiet Environment */}
              <TipCard
                icon="🤫"
                title="סביבה שקטה"
                description="מקום רועש? הסוכנ\ת עלולה להתבלבל"
              />

              {/* Tip 4 - Transcript Accuracy */}
              <TipCard
                icon="📝"
                title="התמליל לא מדויק?"
                description="תתעלמו - הסוכנ\ת מבינ\ה אתכם מצוין"
              />
            </div>

            {/* Start Button */}
            <button
              type="button"
              onClick={handleStartConversation}
              style={{
                width: '100%',
                padding: '14px 24px',
                borderRadius: 8,
                border: 'none',
                background: `linear-gradient(135deg, ${primaryColor}, ${adjustBrightness(primaryColor, 0.8)})`,
                color: 'white',
                fontSize: 16,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: `0 4px 12px ${primaryColor}40`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)'
                e.currentTarget.style.boxShadow = `0 6px 16px ${primaryColor}60`
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = `0 4px 12px ${primaryColor}40`
              }}
            >
              שנתחיל?
            </button>
          </div>
        </motion.div>
      </div>
    </motion.div>
  )
}

// Tip Card Component
const TipCard: React.FC<{
  icon?: string
  iconType?: 'emoji' | 'image'
  iconSrc?: string
  title: string
  description: string
}> = ({ icon, iconType = 'emoji', iconSrc, title, description }) => (
  <div
    style={{
      padding: '12px',
      borderRadius: 8,
      backgroundColor: 'white',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
      transition: 'all 0.2s',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)'
      e.currentTarget.style.transform = 'translateY(-2px)'
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.06)'
      e.currentTarget.style.transform = 'translateY(0)'
    }}
  >
    <div style={{ marginBottom: 6, textAlign: 'center', height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {iconType === 'image' && iconSrc ? (
        <img
          src={iconSrc}
          alt={title}
          style={{
            width: 24,
            height: 24,
            objectFit: 'contain',
          }}
        />
      ) : (
        <span style={{ fontSize: 20 }}>{icon}</span>
      )}
    </div>
    <div style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', marginBottom: 4, textAlign: 'center' }}>
      {title}
    </div>
    <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.4, textAlign: 'center' }}>
      {description}
    </div>
  </div>
)

// Helper function to adjust color brightness
function adjustBrightness(color: string, factor: number): string {
  const hex = color.replace('#', '')
  const r = Math.round(parseInt(hex.substring(0, 2), 16) * factor)
  const g = Math.round(parseInt(hex.substring(2, 4), 16) * factor)
  const b = Math.round(parseInt(hex.substring(4, 6), 16) * factor)
  const toHex = (n: number) => n.toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

